import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ResultOf } from '@graphql-typed-document-node/core';
import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Outlet,
  useLocation,
  useMatch,
  useNavigate,
  useOutlet,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQuery } from 'urql';
import { Picker } from '~/components/nav/Picker';
import { SiteView } from '~/components/site/SiteView';
import { ErrorMessage } from '~/components/ui/Error';
import { Loading } from '~/components/ui/Loading';
import { BottomSheet } from '~/components/ui/nucleus/Sheets/BottomSheet';
import { OutletSheet } from '~/components/ui/nucleus/Sheets/OutletSheet';
import { StatusBadge } from '~/components/ui/StatusBadge';
import {
  JobStatus,
  useAcceptJobMutation,
  useDeclineJobMutation,
  useStartJobMutation,
} from '~/generated/graphql';
import { graphql } from '~/gql';
import { isJobViewDataReady } from '~/helpers/isJobViewDataReady';
import { useTitle } from '~/hooks/index';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useLocationHash } from '~/hooks/useLocationHash';
import { SideLayout } from '~/layouts/side/SideLayout';
import { PopupDialog } from '../../ui/PopupDialog';
import { ConfirmButton } from '../ConfirmDateTime';
import { Accept, Decline, RequestChange } from './Actions';
import { CompleteJobButton } from './CompleteJobButton';
import { Feed } from './Feed';
import { useAddCommentMutation } from './Feed/MessageInput';
import { JobMenu } from './JobMenu';
import { JobReadView } from './JobReadView';

// Compute an absolute list path to "exit" to when closing a job
// Removes trailing task/edit segments and returns a stable list route
function computeJobsListPath(pathname: string): string {
  try {
    let path = pathname.split('#')[0].split('?')[0];

    // Strip any trailing /tasks or /tasks/<...>
    path = path.replace(/\/tasks(?:\/.*)?$/, '');
    // Strip trailing /edit (if present)
    path = path.replace(/\/edit\/?$/, '');

    const segments = path.split('/').filter(Boolean);

    // Activity: go to /activity
    if (segments[0] === 'activity') return '/activity';

    // Nested jobs contexts like /assets/:assetId/jobs/:jobId or /sites/:siteId/jobs/:jobId
    const lastJobsIdx = segments.lastIndexOf('jobs');
    if (lastJobsIdx > 0) {
      // If inside an asset context, prefer returning to the asset root (preserve tab via sessionStorage elsewhere)
      const assetsIdx = segments.lastIndexOf('assets');
      if (assetsIdx !== -1 && assetsIdx < lastJobsIdx) {
        // /assets/:assetId/jobs/... -> /assets/:assetId
        return '/' + segments.slice(0, assetsIdx + 2).join('/');
      }
      // If inside a site context, prefer returning to the site root
      const sitesIdx = segments.lastIndexOf('sites');
      if (sitesIdx !== -1 && sitesIdx < lastJobsIdx) {
        // Handle both /sites/:siteId/jobs/... and .../sites/:siteId/jobs/...
        return '/' + segments.slice(0, sitesIdx + 2).join('/');
      }
      // Otherwise, return the parent jobs list route (e.g., /jobs/<view>)
      return '/' + segments.slice(0, lastJobsIdx + 1).join('/');
    }

    // Root jobs contexts
    if (segments[0] === 'jobs') {
      const view = segments[1];
      const knownViews = new Set([
        'scheduled',
        'planner',
        'completed',
        'declined',
        'offered',
        'calendar',
      ]);
      if (view && knownViews.has(view)) {
        // For calendar view, only keep placeId if segment[2] exists AND segment[3] exists
        // (meaning we're at /jobs/calendar/:placeId/:jobId, not /jobs/calendar/:jobId)
        // This prevents treating a jobId as a placeId when closing a job
        if (view === 'calendar' && segments[2] && segments[3]) {
          return `/jobs/calendar/${segments[2]}`;
        }
        return `/jobs/${view}`;
      }
      // /jobs/:jobId (no view) → /jobs
      return '/jobs';
    }

    // Default safe fallback
    return '/jobs';
  } catch {
    return '/jobs';
  }
}

type ContextType = { job: Job; isOutlet: true; onSuccess: () => void };

const JobQueryDocument = graphql(`
  query Job($id: ID!) {
    job(id: $id) {
      id
      status
      jobNumber
      name
      scheduleStart
      scheduleEnd
      scheduledStartStrict
      scheduledStartDate
      scheduledStartTime
      scheduledEndStrict
      scheduledEndDate
      scheduledEndTime
      timeZone
      completedAt
      attributes {
        id
        type
        category
        name
        value
        attachments {
          id
          kind
          ext
          thumb
          url
          originalFilename
        }
      }
      jobAttributeIds
      jobAttributes {
        id
        value
      }
      jobTags {
        id
        entityType
        name
        category
        description
        colour
      }
      notes
      assigneeNotes
      attachments {
        id
        kind
        ext
        thumb
        url
        originalFilename
      }
      enforceOrder
      isSource
      permissions
      assignees {
        id
        name
        email
      }
      include {
        id
        name
        email
      }
      owners {
        id
        name
        email
      }
      canAcceptOffer
      customer {
        id
        name
      }
      location {
        __typename
        id
        name
        deployment {
          id
          displayLocation(separator: " / ")
        }
        ... on Site {
          address
        }
      }
      tasks {
        # Some fields are required in JobView to support the mandatory tasks feature
        id
        status
        required
        name
        # type is required for getLinkText in TaskListItem
        type
        # description and notes is required for whether or not to show badge in TaskListItem
        description
        notes
        ...TaskData
      }
      owner {
        id
        name
        logo
      }
      booking {
        id
        name
        start
        end
      }
      occupied {
        id
        checkIn: start
        checkOut: end
      }
      prevCheckOut {
        id
        checkIn: start
        checkOut: end
      }
      nextCheckIn {
        id
        checkIn: start
        checkOut: end
      }
      guestyCleaningStatus
      site {
        id
        image
      }
      occupied {
        id
        checkIn: start
        checkOut: end
      }
      prevCheckOut {
        id
        checkIn: start
        checkOut: end
      }
      nextCheckIn {
        id
        checkIn: start
        checkOut: end
      }
    }
  }
`);

export function useJob(options?: { required?: boolean }) {
  const context = useOutletContext<ContextType | undefined>();

  if (options?.required && !context) {
    throw new Error('Must be used in job outlet context');
  }

  return context;
}

export type Job = NonNullable<ResultOf<typeof JobQueryDocument>['job']>;
export type Task = Job['tasks'][0];

type NavigationState = {
  returnTo?: string;
  jobId?: string;
};

type IndexProps = {
  job: Job;
  basePath?: string;
  onClose: () => void;
  isAssetContext?: boolean;
};

// Location state shape used for navigation return targets
type LocationState = { returnTo?: string };

const Index = ({ job, onClose, isAssetContext }: IndexProps) => {
  // const { t } = useTranslation(['translation', 'job']);

  // const [, completeTask] = useCompleteCustomTaskMutation();
  // const [, reset] = useResetTaskStatusMutation();
  // const { isMobile } = useBreakpoint();
  // const [taskToggleLoading, setTaskToggleLoading] = useState<string>('');

  // const toggleTaskStatus: ToggleTaskStatusFn = async ({ id, checked }) => {
  //   try {
  //     setTaskToggleLoading(id);
  //     const { error } = checked
  //       ? await completeTask({
  //           input: { id, date: new Date() },
  //         })
  //       : await reset({ id });

  //     if (error) {
  //       toast.error(error.message.replace('[GraphQL] ', ''));
  //       throw error;
  //     }
  //   } catch (error) {
  //     console.error(error);
  //   } finally {
  //     setTaskToggleLoading('');
  //   }
  // };

  // const GeneratePdfButton = () => {
  //   return (
  //     <button onClick={() => window.print()}>
  //       <FontAwesomeIcon
  //         icon={faFilePdf}
  //         color='#506262'
  //         size='xl'
  //         className='hover:text-brand'
  //       />
  //     </button>
  //   );
  // };

  const [showSite, setShowSite] = useState<boolean>(false);
  const [showFeed, setShowFeed] = useLocationHash('#messages-open');
  // const [isVisible, setIsVisible] = useState(false);
  const [requiredTasks, setRequiredTasks] = useState<Task[]>([]);

  useEffect(() => {
    if (job.tasks.length > 0) {
      setRequiredTasks(
        job.tasks.filter(
          (task) => task.required && task.status === JobStatus.Created
        )
      );
    }
  }, [job]);

  const handleMandatoryTasksSubmit = (event: Event) => {
    if (requiredTasks.length > 0) {
      event.stopImmediatePropagation();

      toast.info(
        `Please complete or defer ${requiredTasks
          .map((task) => task.name)
          .join(', ')} before completing this Job`,
        {
          theme: 'light',
          className: 'bg-[#008F90] text-white',
          icon: <FontAwesomeIcon icon={faExclamationTriangle} />,
        }
      );
    }
  };

  useEffect(() => {
    // If there are incomplete mandatory tasks, this stops propagation of job complete button and show toast message
    const completeBtnEl = document
      .querySelector('#complete-btn-wrapper')
      ?.querySelector('button');

    if (requiredTasks.length < 1 || !completeBtnEl) return;

    completeBtnEl.addEventListener('click', handleMandatoryTasksSubmit);
    return () => {
      completeBtnEl.removeEventListener('click', handleMandatoryTasksSubmit);
    };
  }, [requiredTasks]);

  const site = job.location?.__typename === 'Site' ? job.location : undefined;

  return showFeed ? (
    <Feed job={job} onTabChange={setShowFeed} onClose={onClose} />
  ) : (
    <SideLayout willOverflow data-usetiful='job-view'>
      <SideLayout.Head
        rightSlot={
          job.permissions.length > 0 ? (
            <JobMenu job={job} />
          ) : // <GeneratePdfButton />
          null
        }
        // secondRightSlot={
        //   !isMobile && job.permissions.includes('edit') && <GeneratePdfButton />
        // }
        onClose={onClose}
        iconClass={'x'}
      >
        <div className='flex w-full flex-col items-center'>
          <h5 data-usetiful='job-name' className='leading-5 text-[#031121]'>
            {job.jobNumber} - {job.name}
          </h5>

          <div className='mb-2 mt-[5px] flex text-base font-medium leading-[17px] text-[#005959]'>
            <span className='text-[#7F909C]'>For</span>
            <span className='ml-[3px]'>{job.owner.name}</span>
            {/* <div
              className='flex'
              onMouseEnter={() => setIsVisible(true)}
              onMouseLeave={() => {
                setIsVisible(false);
              }}
            >
              <FontAwesomeIcon
                icon={faInfoCircle}
                className='hover:cursor-pointer'
              />
              <Transition
                className='sticky top-8 z-10 w-full'
                show={isVisible}
                enter='ease-out duration-300'
                enterFrom='opacity-0 scale-95'
                enterTo='opacity-100 scale-100'
                leave='ease-in duration-50'
                leaveFrom='opacity-100 scale-100'
                leaveTo='opacity-50 scale-95'
              >
                <AccommodationProvider owner={job.owner} />
              </Transition>
            </div> */}
          </div>

          <StatusBadge value={job.status} />
        </div>
      </SideLayout.Head>

      <PopupDialog isOpen={showSite} onClose={() => setShowSite(false)}>
        <PopupDialog.Title>{site?.name}</PopupDialog.Title>
        {job.location && (
          <SiteView
            isPopup={true}
            siteId={job.location.id}
            onClose={() => setShowSite(false)}
          />
        )}
      </PopupDialog>

      <div className='px-4 py-[2px] print:hidden'>
        <Picker value='details' className='rounded-full'>
          <Picker.Button
            id='details'
            onClick={() => setShowFeed(false)}
            className='rounded-l-full'
          >
            Details
          </Picker.Button>
          <Picker.Button
            id='messages'
            onClick={() => setShowFeed(true)}
            className='rounded-r-full'
          >
            Messages & History
          </Picker.Button>
        </Picker>
      </div>

      <>
        <SideLayout.Body className='relative p-4'>
          <JobReadView job={job} />
        </SideLayout.Body>

        <SideLayout.Foot className='!border-t-0 p-4 text-center print:hidden'>
          {job.status === JobStatus.Complete && (
            <div onClick={onClose} className='cursor-pointer p-3'>
              <div style={{ fontWeight: 500, color: 'var(--color-green)' }}>
                CLOSE
              </div>
            </div>
          )}
          {job.status === JobStatus.InProgress && (
            <div id='complete-btn-wrapper'>
              <CompleteJobButton id={job.id} enabled={true} />
            </div>
          )}
          {job.status === JobStatus.Accepted && <StartJobButton id={job.id} />}
          {job.status === JobStatus.Offered && job.canAcceptOffer && (
            <JobOfferActions job={job} />
          )}
        </SideLayout.Foot>
      </>
    </SideLayout>
  );
};

type Props = {
  jobId: string;
  basePath?: string;
};

const MobileOutlet = ({
  outlet,
  job,
  currentTask,
  handleTaskSuccess,
  navigate,
  isPopup,
  isStacked,
}: {
  outlet: unknown;
  job: Job;
  currentTask: Task | undefined;
  handleTaskSuccess: () => void;
  navigate: ReturnType<typeof useNavigate>;
  isPopup: boolean;
  isStacked?: boolean;
}): JSX.Element | null => {
  // Mobile sheet title: use task name (asset popups within tasks use PopupDialogNew which passes title)
  const sheetTitle = currentTask?.name;

  if (!outlet) return null;

  return isPopup ? (
    <BottomSheet
      isOpen={!!outlet}
      onClose={() => {
        navigate(-1);
      }}
      title={sheetTitle}
    >
      <Outlet
        context={{
          job,
          isOutlet: true,
          onSuccess: handleTaskSuccess,
        }}
      />
    </BottomSheet>
  ) : (
    <OutletSheet
      animation='fade'
      isOpen={!!outlet}
      onClose={() => {
        navigate(-1);
      }}
      isStacked={isStacked}
    >
      <Outlet context={{ job, isOutlet: true, onSuccess: handleTaskSuccess }} />
    </OutletSheet>
  );
};

/**
 * @todo Finish merge into routes/jobs/$jobId route component
 */
export const JobView = ({ jobId }: Props) => {
  // ALL HOOKS MUST BE CALLED FIRST - BEFORE ANY EARLY RETURNS
  const outlet = useOutlet();
  const matchSites = useMatch('/sites/*');
  const matchAssets = useMatch('/assets/*');
  const matchPlanner = useMatch('/jobs/planner/*');
  const matchMonthlyCalendar = useMatch('/jobs/calendar/*');
  const { pathname } = useLocation();
  const { taskId } = useParams();

  const [{ fetching, data }, reexecuteQuery] = useQuery({
    query: JobQueryDocument,
    variables: { id: jobId },
    requestPolicy: 'cache-first',
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useBreakpoint();

  // Track origin path (DayView) robustly using state or sessionStorage
  const originRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const fromState = (location.state as LocationState)?.returnTo;
    if (fromState) {
      // Always honor explicit returnTo provided by navigation
      originRef.current = fromState;
      try {
        sessionStorage.setItem(`returnTo_job_${jobId}`, fromState);
      } catch {
        // sessionStorage may be unavailable (e.g., disabled or restricted environments)
      }
    } else {
      // Only fall back to sessionStorage for planner/day context.
      // For monthly calendar context, require explicit state to avoid stale day-view returns.
      if (matchPlanner) {
        try {
          const stored = sessionStorage.getItem(`returnTo_job_${jobId}`);
          if (stored) originRef.current = stored;
        } catch {
          // sessionStorage may be unavailable (e.g., disabled or restricted environments)
        }
      } else {
        // Do not use any stale stored returnTo when in monthly calendar context without explicit state
        originRef.current = undefined;
      }
    }
  }, [location.state, jobId, matchPlanner]);

  // Track last refetch time to prevent excessive re-fetching during task navigation
  const lastRefetchRef = useRef<number>(0);
  const REFETCH_DEBOUNCE_MS = 2000; // Minimum time between refetches

  useTitle(
    data?.job ? [data.job.jobNumber, data.job.name].join(' ') : undefined
  );

  // Improved task success handler with debouncing and smarter cache policy
  // MUST be defined here at the top level, before any early returns
  const handleTaskSuccess = useCallback(() => {
    const now = Date.now();

    // Debounce refetches to prevent rapid successive calls during task navigation
    if (now - lastRefetchRef.current < REFETCH_DEBOUNCE_MS) {
      return;
    }

    lastRefetchRef.current = now;

    // Use cache-first policy instead of cache-and-network to be less aggressive
    // This will only hit the network if the cache is stale or empty
    reexecuteQuery({ requestPolicy: 'cache-first' });
  }, [reexecuteQuery]);

  // NOW SAFE TO HAVE EARLY RETURNS - ALL HOOKS HAVE BEEN CALLED
  const isPopup =
    pathname.includes('bookings') || pathname.includes('contacts');

  if (fetching)
    return (
      <div className='flex items-center justify-center lg:w-rsb'>
        <Loading spinner />
      </div>
    );

  if (!data?.job)
    return (
      <div className='lg:w-rsb'>
        <ErrorMessage message='Job not found' />
      </div>
    );

  // Cache-first can keep the previous job while the route jobId has already
  // changed. Assignee notes and tags live in local state and save by job.id,
  // so rendering the stale job lets those writes land on the newly selected job.
  if (!isJobViewDataReady(data.job, jobId)) {
    return (
      <div className='flex items-center justify-center lg:w-rsb'>
        <Loading spinner />
      </div>
    );
  }

  const job = data.job;
  const currentTask = job.tasks.find((t) => t.id === taskId);

  const handleClose = () => {
    const fromStateReturnTo = (location.state as LocationState)?.returnTo;
    const isDayContext = Boolean(matchPlanner || matchMonthlyCalendar);
    const effectiveReturnTo =
      fromStateReturnTo ?? (isDayContext ? originRef.current : undefined);

    // On desktop, prefer going back in history to return to the originating view
    if (!isMobile) {
      // If a specific returnTo path is provided (e.g., monthly day view), use it
      if (effectiveReturnTo) {
        // Use replace to avoid creating navigation history that can cause loops
        navigate(effectiveReturnTo, { replace: true });
        return;
      }

      // If we're inside the jobs list context (/jobs/{view}/{jobId}[...]), go back in history
      // This avoids unnecessary re-navigation that can cause the list to refetch or remount
      const jobsListContextMatch = pathname.match(
        /^\/jobs\/(scheduled|completed|unscheduled)\/[A-Za-z0-9_-]+(\/.*)?$/
      );
      if (jobsListContextMatch) {
        navigate(-1);
        return;
      }

      // Desktop: if inside assets context, preserve the last-selected asset tab (default to Jobs tab = 1)
      if (matchAssets) {
        const assetMatch = pathname.match(/^\/assets\/([^/]+)/);
        const assetIdFromPath = assetMatch?.[1];
        const storedTab = assetIdFromPath
          ? sessionStorage.getItem(`assetTab_${assetIdFromPath}`)
          : null;
        const tabParam = storedTab ? `?tab=${storedTab}` : `?tab=1`;
        if (assetIdFromPath) {
          navigate(`/assets/${assetIdFromPath}${tabParam}`, { replace: true });
          return;
        }
      }

      const jobsListPath = computeJobsListPath(pathname);
      navigate(jobsListPath, { replace: true });
      return;
    }

    // Mobile specific behavior (preserve previous state handling)
    if (matchAssets) {
      const assetMatch = pathname.match(/^\/assets\/(\d+)/);
      const assetId = assetMatch?.[1];
      const storedTab = assetId
        ? sessionStorage.getItem(`assetTab_${assetId}`)
        : null;
      const tabParam = storedTab ? `?tab=${storedTab}` : '';
      const targetPath = `../..${tabParam}`;
      navigate(targetPath, { state: { jobId: job.id } });
      return;
    }

    // Mobile: Use returnTo if available (only when in day context or explicitly provided in state)
    if (effectiveReturnTo) {
      navigate(effectiveReturnTo, { replace: true });
      return;
    }

    // Mobile: Deterministic absolute fallback
    const jobsListPath = computeJobsListPath(pathname);

    navigate(jobsListPath, { replace: true });
  };

  return (
    <div
      className={classNames(
        'absolute h-full w-full lg:relative lg:w-auto',
        matchSites || matchAssets
          ? 'block'
          : 'flex h-full items-stretch justify-start'
      )}
    >
      <Index
        key={job.id}
        job={job}
        onClose={handleClose}
        isAssetContext={!!matchAssets}
      />

      {/* Mobile rendering */}
      {isMobile && currentTask && (
        <MobileOutlet
          outlet={outlet}
          job={job}
          currentTask={currentTask}
          handleTaskSuccess={handleTaskSuccess}
          navigate={navigate}
          isPopup={isPopup}
          isStacked={true}
        />
      )}

      {/* Desktop rendering */}
      {!isMobile && outlet && (
        <>
          {isPopup ? (
            <Outlet
              context={{ job, isOutlet: true, onSuccess: handleTaskSuccess }}
            />
          ) : (
            <div
              className={classNames(
                'absolute z-50 h-full w-full min-w-0 flex-1 overflow-y-auto',
                matchSites ||
                  matchAssets ||
                  matchPlanner ||
                  matchMonthlyCalendar
                  ? 'inset-0 z-[8999]'
                  : 'z-50 xl:relative'
              )}
            >
              <Outlet
                context={{ job, isOutlet: true, onSuccess: handleTaskSuccess }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Note: previously included AddToCalendarLink and SendJobButton components were
// removed due to being unused within this module, resolving linter errors.

export function StartJobButton({ id }: { id: string }) {
  const { t } = useTranslation('job');
  const [, start] = useStartJobMutation();

  return (
    <ConfirmButton
      title='StartTime'
      onConfirm={async (date) => {
        await start({ id, startTime: date });
      }}
    >
      {t('startJob')}
    </ConfirmButton>
  );
}

function JobOfferActions({ job }: { job: Job }) {
  const [, accept] = useAcceptJobMutation();
  const [, decline] = useDeclineJobMutation();
  const [, addComment] = useAddCommentMutation();

  return (
    <>
      <div className='-mb-2 flex gap-4 pt-1'>
        <Accept jobId={job.id} jobName={job.name} onAccept={accept} />
        <Decline jobId={job.id} jobName={job.name} onDecline={decline} />
        <span />
      </div>
      <RequestChange jobId={job.id} onSubmit={addComment} />
    </>
  );
}
